import React, { useState, useCallback } from 'react';
import {
  Box,
  TextField,
  Grid,
  Typography,
} from '@mui/material';
import ValidationFeedback from './ValidationFeedback';

export interface AddressInput {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

export interface AddressValidationResult {
  success: boolean;
  validated: boolean;
  address?: {
    street: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    formattedAddress: string;
    latitude: number;
    longitude: number;
    confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  };
  error?: {
    code: string;
    message: string;
    retryable: boolean;
  };
  metadata?: {
    responseTime: number;
    cached: boolean;
    provider: string;
  };
}

interface AddressValidationInputProps {
  value: AddressInput;
  onChange: (address: AddressInput) => void;
  onValidationChange: (result: AddressValidationResult | null) => void;
  required?: boolean;
  label?: string;
  disabled?: boolean;
  showValidationErrors?: boolean; // New prop to show field validation errors
}

const AddressValidationInput: React.FC<AddressValidationInputProps> = ({
  value,
  onChange,
  onValidationChange,
  required = false,
  label = 'Address',
  disabled = false,
  showValidationErrors = false,
}) => {
  const [validationResult, setValidationResult] = useState<AddressValidationResult | null>(null);

  const handleFieldChange = useCallback((field: keyof AddressInput, fieldValue: string) => {
    const newAddress = { ...value, [field]: fieldValue };
    onChange(newAddress);
    
    // Clear validation result when address changes
    if (validationResult) {
      setValidationResult(null);
      onValidationChange(null);
    }
  }, [value, onChange, validationResult, onValidationChange]);

  // Check if any field has data (making all fields required)
  const hasAnyData = !!(value.street?.trim() || value.city?.trim() || value.state?.trim() || value.zip?.trim());

  // Validation helper - only show errors when explicitly requested (on save)
  const getFieldError = (fieldValue?: string) => {
    return showValidationErrors && hasAnyData && !fieldValue?.trim();
  };

  return (
    <Box>
      {label && (
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'medium' }}>
          {label} {(required || hasAnyData) && '*'}
        </Typography>
      )}
      
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Street Address"
            fullWidth
            value={value.street || ''}
            onChange={(e) => handleFieldChange('street', e.target.value)}
            disabled={disabled}
            required={hasAnyData}
            error={getFieldError(value.street)}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            label="City"
            fullWidth
            value={value.city || ''}
            onChange={(e) => handleFieldChange('city', e.target.value)}
            disabled={disabled}
            required={hasAnyData}
            error={getFieldError(value.city)}
          />
        </Grid>
        
        <Grid item xs={12} sm={3}>
          <TextField
            label="State"
            fullWidth
            value={value.state || ''}
            onChange={(e) => handleFieldChange('state', e.target.value)}
            disabled={disabled}
            required={hasAnyData}
            error={getFieldError(value.state)}
          />
        </Grid>
        
        <Grid item xs={12} sm={3}>
          <TextField
            label="ZIP Code"
            fullWidth
            value={value.zip || ''}
            onChange={(e) => handleFieldChange('zip', e.target.value)}
            disabled={disabled}
            required={hasAnyData}
            error={getFieldError(value.zip)}
          />
        </Grid>
      </Grid>

      {validationResult && (
        <Box sx={{ mt: 2 }}>
          <ValidationFeedback result={validationResult} />
        </Box>
      )}
    </Box>
  );
};

export default AddressValidationInput;
